/**
 * The vocabulary a booking capability speaks, shared by every adapter that
 * exposes it (WebMCP today, a remote MCP server later). Parse at the edge so
 * everything downstream holds a value that is already known-good.
 */
import { z } from 'zod';
import { InstrumentLabels, type Instrument } from '@/app/instruments';
import { AgeGroupLabels, type AgeGroup } from '@/config/agegroupLabels';
import { leadSourceLabels, type LeadSource } from '@/config/leadSourceLabels';

function keysOf<T extends string>(labels: Record<T, string>): [T, ...T[]] {
  const keys = Object.keys(labels) as T[];
  return keys as [T, ...T[]];
}

export const instrumentSchema = z.enum(keysOf<Instrument>(InstrumentLabels));
export const ageGroupSchema = z.enum(keysOf<AgeGroup>(AgeGroupLabels));
export const leadSourceSchema = z.enum(keysOf<LeadSource>(leadSourceLabels));

export const lessonLocationSchema = z.enum(['studio', 'online']);
export type LessonLocation = z.infer<typeof lessonLocationSchema>;

/**
 * One student's trial request. Deliberately the single-student shape only:
 * family enrolment is a branch a human is better at than an agent, and the
 * form still offers it.
 */
export const trialBookingDraftSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  email: z.string().trim().email('A valid email is required'),
  phone: z.string().trim().min(8, 'A valid phone number is required'),
  instrument: instrumentSchema,
  ageGroup: ageGroupSchema,
  about: z.string().trim().max(500).optional(),
  country: z.string().trim().length(2).default('AU'),
  location: lessonLocationSchema.default('studio'),
  // An agent-assisted visitor arrived through an AI surface by definition, and
  // `aisearch` is an answer the form already offers.
  leadSource: leadSourceSchema.default('aisearch'),
});

export type TrialBookingDraft = z.infer<typeof trialBookingDraftSchema>;
export type TrialBookingDraftInput = z.input<typeof trialBookingDraftSchema>;
